import { Cloud, Globe, Link, X, type LucideIcon } from 'lucide-react';
import { site } from '@/site.config';

interface SocialLink {
  icon: LucideIcon;
  label: string;
  href: string;
}

interface NavLink {
  label: string;
  href: string;
}

type LinkColumns = Record<string, NavLink[]>;

const links: LinkColumns = {
  Services: [
    { label: 'AI Consulting', href: '/#services' },
    { label: 'Cloud Infrastructure', href: '/#services' },
    { label: 'Workflow Automation', href: '/#services' },
    { label: 'DevOps', href: '/#services' },
  ],
  Company: [
    { label: 'About', href: '/#about' },
    { label: 'Contact', href: '/#contact' },
  ],
  Legal: [
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Terms of Service', href: '/terms' },
  ],
};

const social: SocialLink[] = [
  { icon: Globe, label: 'GitHub', href: '#' },
  { icon: Link, label: 'LinkedIn', href: '#' },
  { icon: X, label: 'X (Twitter)', href: '#' },
];

export default function Footer() {
  return (
    <footer className="relative bg-black border-t border-white/[0.05]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 mb-12">
          <div className="lg:col-span-2">
            <a href="/" className="flex items-center gap-2.5 mb-5 group w-fit">
              <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/30">
                <Cloud className="text-white" size={18} />
              </div>
              <span className="text-white font-semibold text-[15px]">
                Mataly <span className="text-blue-400">Cloud</span>
              </span>
            </a>
            <p className="text-slate-500 text-sm leading-relaxed max-w-xs mb-6">
              {site.description}
            </p>
            <div className="flex items-center gap-3">
              {social.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  className="w-9 h-9 rounded-lg bg-white/[0.04] border border-white/[0.07] flex items-center justify-center text-slate-500 hover:text-white hover:border-blue-500/30 transition-all duration-200"
                >
                  <s.icon size={15} />
                </a>
              ))}
            </div>
          </div>

          {Object.entries(links).map(([heading, items]) => (
            <div key={heading}>
              <h3 className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-4">
                {heading}
              </h3>
              <ul className="flex flex-col gap-2.5">
                {items.map((item) => (
                  <li key={item.label}>
                    <a
                      href={item.href}
                      className="text-sm text-slate-500 hover:text-slate-300 transition-colors duration-200"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-white/[0.05] pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-600">
            © {new Date().getFullYear()} {site.name}. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-xs text-slate-600">
            <a href="/privacy" className="hover:text-slate-400 transition-colors">
              Privacy
            </a>
            <a href="/terms" className="hover:text-slate-400 transition-colors">
              Terms
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
